/**
 * Correcting a medication order (DECISIONS.md #46).
 *
 * **Corrections only. There is no way to prescribe here**, and that is the decision rather than a
 * gap: a first version of a `medication_request` *is* an ordination, which needs the prescriber's
 * authority, a product with a strength and a beredningsform, and the indication HSLF-FS 2017:37
 * requires — none of which phase 0 has a screen for. What it does have is a ward full of orders
 * transcribed from a bundle during an outage, one of which has the wrong dose in it, and no way to
 * say so. That is what this fixes. The form says it out loud (`screen.notPrescribing`) so nobody
 * reads the absence as a bug.
 *
 * **Half the order is inherited and shown read-only** (#46 point 2): substance, strength, dose form,
 * start, prescriber. Those say *which fact this row is about*, and a correction that could change
 * them would not be a correction of this order, it would be a different order wearing its
 * `record_id`.
 *
 * **The dose crosses the wire as the text that was typed** (#46 point 6). `0,25` is refused, never
 * read as `0.25`: the two differ by nothing a tired reader notices and by a factor of ten in the
 * dose, which is the error `I18N.md` §4 exists to prevent. The **unit** is refused server-side too,
 * and that matters more here than anywhere else — `formatDose` *throws* rather than render something
 * misreadable, and this order is on a panel that must be readable without scrolling (AGENTS.md §5).
 * A unit the record accepted but the formatter cannot render would blank a dose at the bedside.
 *
 * **Nothing is computed** (#46 point 8). The maximum dose per 24 hours is typed as it was ordered,
 * not derived from the dose and the frequency — deriving it is dose calculation, which is the MDR
 * class IIa line (AGENTS.md §3 rule 5).
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatClinicalDateTime, parseClinicalDateTime } from '../format/clinicalDate'
import {
  correctMedication,
  MEDICATION_STATUSES,
  MedicationRefusedError,
  MedicationUnavailableError,
  type CorrectedMedication,
  type MedicationCorrectionSubmission,
  type MedicationRefusalCode,
  type MedicationRefusalParams,
} from './medicationApi'
import {
  medicationRefusalMessage,
  medicationStatusLabel,
  medicationUnavailableMessage,
} from './medicationDisplay'
import type { PatientMedication } from './patientOverviewApi'

/** Mirrors `MedicationCorrection`; see {@link AllergyForm} for why this is not load-bearing. */
const MAXIMUM_SHORT_FIELD_LENGTH = 200

/** The indication is a sentence, and the law requires it. */
const MAXIMUM_INDICATION_LENGTH = 1000

/** The form's fields, all as text apart from the one that is genuinely a yes or no. */
interface MedicationDraft {
  readonly status: string
  readonly doseAmount: string
  readonly doseUnit: string
  readonly route: string
  readonly frequency: string
  readonly isPrn: boolean
  readonly prnMaxDose24h: string
  /** indication = ordinationsorsak, mandatory under HSLF-FS 2017:37 */
  readonly indication: string
  /** `YYYY-MM-DD HH:mm` as typed, local time — the format every clinical time is read in. */
  readonly endAt: string
}

/**
 * The instant the record holds, as `YYYY-MM-DD HH:mm` local time for the box.
 *
 * Through the shared formatter, so the box shows an end date exactly as the order list beside it
 * does. It was a `datetime-local` control until 2026-10-07, and that renders in the browser's
 * locale — `mm/dd/yyyy --:-- AM/PM` in an English-US Chrome, the foreign medical team I18N.md §6
 * is written for — while §5 says a clinical time is `YYYY-MM-DD HH:mm` whatever the language. The
 * registration form had already refused that control for the birth date, for this reason.
 *
 * An unparseable instant becomes an empty box rather than a guess. Saving then sends no end date,
 * which is a change the clinician can see in the field before they press Save.
 */
function toLocalInput(instant: string | null): string {
  if (instant === null) {
    return ''
  }

  try {
    return formatClinicalDateTime(instant)
  } catch {
    return ''
  }
}

/**
 * The typed local time back into the UTC instant the server parses.
 *
 * `parseClinicalDateTime` is strict, so only a value naming exactly one local minute becomes an
 * instant, and `toISOString` gives the `…Z` form `MedicationCorrection` requires.
 *
 * **Anything else is sent as typed, not dropped and not repaired.** The server accepts nothing
 * without a `Z`, so it refuses it (`MEDICATION_END_AT_MALFORMED`) and says so; a client that
 * quietly sent null instead would write a version with no end date and report success, which is
 * #46 point 6's whole objection to repairing a value rather than refusing it.
 */
function toInstant(typed: string): string | null {
  const trimmed = typed.trim()
  if (trimmed === '') {
    return null
  }

  const parsed = parseClinicalDateTime(trimmed)
  return parsed === null ? typed : parsed.toISOString()
}

/**
 * The dose the record holds, as text for the box.
 *
 * `String` on a `number`, which gives `0.25`, `5` and `100` — decimal point, leading zero present,
 * no trailing zero. That is `I18N.md` §4's rule by coincidence rather than by construction, and it
 * holds because the value came out of a `numeric` column the shared formatter has already rendered
 * on the panel behind this form.
 */
function doseText(amount: number): string {
  return Number.isFinite(amount) ? String(amount) : ''
}

/** See {@link AllergyForm}'s equivalent for why this is derived from the prop rather than copied. */
function draftFrom(medication: PatientMedication): MedicationDraft {
  return {
    status: 'active',
    doseAmount: doseText(medication.doseAmount),
    doseUnit: medication.doseUnit,
    route: medication.route,
    frequency: medication.frequency,
    isPrn: medication.isPrn,
    prnMaxDose24h: medication.prnMaxDose24h ?? '',
    indication: medication.indication,
    endAt: toLocalInput(medication.endAt),
  }
}

function isDirty(draft: MedicationDraft, original: MedicationDraft): boolean {
  return (
    draft.status !== original.status ||
    draft.doseAmount !== original.doseAmount ||
    draft.doseUnit !== original.doseUnit ||
    draft.route !== original.route ||
    draft.frequency !== original.frequency ||
    draft.isPrn !== original.isPrn ||
    draft.prnMaxDose24h !== original.prnMaxDose24h ||
    draft.indication !== original.indication ||
    draft.endAt !== original.endAt
  )
}

function toSubmission(draft: MedicationDraft, supersedes: string): MedicationCorrectionSubmission {
  const maximum = draft.prnMaxDose24h.trim()

  return {
    supersedes,
    status: draft.status,
    // Untrimmed. The dose and its unit are refused rather than repaired, and `IE` is stored as typed
    // so its capital letters survive — trimming is a small repair, and there is no such thing as a
    // small repair to a dose.
    doseAmount: draft.doseAmount,
    doseUnit: draft.doseUnit,
    route: draft.route.trim(),
    frequency: draft.frequency.trim(),
    isPrn: draft.isPrn,
    // Null when the order is not vid behov, whatever is still in the box: the field is hidden in that
    // state, and a maximum per 24 hours on an order that is not PRN is a number with nothing to be
    // the maximum of.
    prnMaxDose24h: draft.isPrn ? (maximum === '' ? null : maximum) : null,
    indication: draft.indication.trim(),
    endAt: toInstant(draft.endAt),
  }
}

type Phase =
  | { readonly kind: 'editing' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'corrected' }

type Notice =
  | {
      readonly kind: 'refused'
      readonly code: MedicationRefusalCode | 'UNKNOWN'
      readonly params: MedicationRefusalParams
    }
  | { readonly kind: 'unavailable' }

/**
 * The medication correction form.
 *
 * @param patientId Whose record this is written to.
 * @param medication The order being corrected. Read on every render, so a refetch after a stale
 *   refusal moves both the inherited context and the comparison the unsaved-work guard makes.
 * @param onCorrected A new version was written; the overview refetches.
 * @param onStale Somebody else corrected this order. Refetches and keeps the draft.
 * @param onClose Leaves the form.
 */
export function MedicationCorrectionForm({
  patientId,
  medication,
  onCorrected,
  onStale,
  onClose,
}: {
  readonly patientId: string
  readonly medication: PatientMedication
  readonly onCorrected: (corrected: CorrectedMedication) => void
  readonly onStale: () => void
  readonly onClose: () => void
}) {
  const { t } = useTranslation(['common', 'patient', 'medication'])

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' })
  const [draft, setDraft] = useState<MedicationDraft>(() => draftFrom(medication))
  const [notice, setNotice] = useState<Notice | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  /** See {@link AllergyForm}: a submit in flight is not aborted on unmount. */
  const live = useRef(true)
  // Set on every mount, not only by the ref's initial value: React StrictMode mounts, unmounts and
  // mounts again in development, and a flag only ever cleared would stay false after that, so a
  // save would land and the form never say so (review of 2026-10-12). Production renders once.
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const original = draftFrom(medication)
  const dirty = isDirty(draft, original)
  const busy = phase.kind === 'saving'

  useEffect(() => {
    if (!dirty || phase.kind === 'corrected') {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()

      // Both, for the reason `NoteEditor` gives.
      event.returnValue = 'unsaved'
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, phase.kind])

  const update = <Field extends keyof MedicationDraft>(
    field: Field,
    value: MedicationDraft[Field],
  ) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
    setNotice(null)
  }

  const submit = () => {
    if (busy) {
      return
    }

    setPhase({ kind: 'saving' })
    setNotice(null)

    const controller = new AbortController()

    // `medication.versionId` read here and not at mount: it is the version this Save supersedes, and
    // after a stale refusal and a refetch it is a different one.
    correctMedication(
      patientId,
      medication.medicationId,
      toSubmission(draft, medication.versionId),
      controller.signal,
    ).then(
      (result) => {
        if (!live.current) {
          return
        }
        setPhase({ kind: 'corrected' })
        onCorrected(result)
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }

        setPhase({ kind: 'editing' })

        if (cause instanceof MedicationRefusedError) {
          setNotice({ kind: 'refused', code: cause.code, params: cause.params })

          if (cause.code === 'MEDICATION_VERSION_STALE') {
            onStale()
          }
        } else if (cause instanceof MedicationUnavailableError) {
          setNotice({ kind: 'unavailable' })
        } else {
          setNotice({ kind: 'refused', code: 'UNKNOWN', params: {} })
        }
      },
    )
  }

  const close = () => {
    if (dirty && phase.kind === 'editing') {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  if (phase.kind === 'corrected') {
    return (
      <section className="registration patient-correction" aria-labelledby="medication-form-heading">
        <h3 id="medication-form-heading">{t('medication:screen.heading')}</h3>

        <p className="registration__registered" role="status">
          {t('medication:screen.saved')}
        </p>

        <button type="button" onClick={onClose}>
          {t('medication:screen.close')}
        </button>
      </section>
    )
  }

  const prescriber = `${medication.prescriberGivenName} ${medication.prescriberFamilyName}`

  return (
    <section className="registration patient-correction" aria-labelledby="medication-form-heading">
      <header className="registration__header">
        <h3 id="medication-form-heading">{t('medication:screen.heading')}</h3>

        <p className="registration__explains">{t('medication:screen.explanation')}</p>

        {/* Said before the fields rather than after a refusal: a clinician who came here to add an
            order needs to know that in the first sentence, not at the bottom. */}
        <p className="registration__explains">{t('medication:screen.notPrescribing')}</p>
      </header>

      {/* The part of the order a correction does not touch. A definition list rather than disabled
          inputs: these are not fields whose editing is switched off, they are the order's identity,
          and boxes with the cursor blocked look like a bug. */}
      <dl className="registration__field">
        <dt>{t('patient:medication.columnSubstance')}</dt>
        <dd>{medication.substance}</dd>

        <dt>{t('patient:medication.columnPreparation')}</dt>
        <dd>
          {t('patient:medication.preparation', {
            strength: medication.strength,
            form: medication.doseForm,
          })}
        </dd>

        <dt>{t('patient:medication.columnOrdered')}</dt>
        {/* Through the shared formatter, always `YYYY-MM-DD HH:mm` whatever the interface language
            (I18N.md §3). */}
        <dd>{formatClinicalDateTime(medication.startAt)}</dd>

        <dt>{t('medication:field.prescriber')}</dt>
        <dd>{prescriber}</dd>
      </dl>

      <p className="registration__hint">{t('medication:screen.inherited')}</p>

      <p className="registration__hint">{t('medication:screen.requiredHint')}</p>

      <div className="registration__field">
        <label htmlFor="medication-field-dose-amount">
          {t('patient:medication.columnDose')}
        </label>

        <p id="medication-field-dose-amount-hint" className="registration__hint">
          {t('medication:fieldHint.doseAmount')}
        </p>

        <input
          id="medication-field-dose-amount"
          // `text`, not `number`: a number input hands the browser the decimal separator, and which
          // one it accepts depends on the machine's locale. The record must see what was typed so it
          // can refuse a comma itself (#46 point 6). The spinner would also let a scroll change a
          // dose by accident.
          type="text"
          inputMode="decimal"
          value={draft.doseAmount}
          disabled={busy}
          autoFocus
          aria-describedby="medication-field-dose-amount-hint"
          maxLength={MAXIMUM_SHORT_FIELD_LENGTH}
          onChange={(event) => update('doseAmount', event.target.value)}
        />
      </div>

      <div className="registration__field">
        <label htmlFor="medication-field-dose-unit">{t('medication:field.doseUnit')}</label>

        <p id="medication-field-dose-unit-hint" className="registration__hint">
          {t('medication:fieldHint.doseUnit')}
        </p>

        <input
          id="medication-field-dose-unit"
          // Free text rather than a picker, and safe because the server's blocklists are the same two
          // sets `format/clinicalDose.ts` holds: anything the record accepts, the formatter renders.
          // A picker would have to enumerate every unit any ward uses, and the one it was missing
          // would be the one somebody needed at 03:00.
          type="text"
          value={draft.doseUnit}
          disabled={busy}
          aria-describedby="medication-field-dose-unit-hint"
          maxLength={MAXIMUM_SHORT_FIELD_LENGTH}
          onChange={(event) => update('doseUnit', event.target.value)}
        />
      </div>

      <div className="registration__field">
        <label htmlFor="medication-field-route">{t('patient:medication.columnRoute')}</label>

        <p id="medication-field-route-hint" className="registration__hint">
          {t('medication:fieldHint.route')}
        </p>

        <input
          id="medication-field-route"
          type="text"
          value={draft.route}
          disabled={busy}
          aria-describedby="medication-field-route-hint"
          maxLength={MAXIMUM_SHORT_FIELD_LENGTH}
          onChange={(event) => update('route', event.target.value)}
        />
      </div>

      <div className="registration__field">
        <label htmlFor="medication-field-frequency">{t('patient:medication.columnSchedule')}</label>

        <p id="medication-field-frequency-hint" className="registration__hint">
          {t('medication:fieldHint.frequency')}
        </p>

        <input
          id="medication-field-frequency"
          type="text"
          value={draft.frequency}
          disabled={busy}
          aria-describedby="medication-field-frequency-hint"
          maxLength={MAXIMUM_SHORT_FIELD_LENGTH}
          onChange={(event) => update('frequency', event.target.value)}
        />
      </div>

      <div className="registration__field">
        <label htmlFor="medication-field-is-prn">{t('medication:field.isPrn')}</label>

        <p id="medication-field-is-prn-hint" className="registration__hint">
          {t('medication:fieldHint.isPrn')}
        </p>

        <input
          id="medication-field-is-prn"
          type="checkbox"
          checked={draft.isPrn}
          disabled={busy}
          aria-describedby="medication-field-is-prn-hint"
          onChange={(event) => update('isPrn', event.target.checked)}
        />
      </div>

      {/* Only for a vid behov order, where the record requires it. Hidden rather than disabled, for
          the reason the substance field gives: a greyed box is a question. */}
      {draft.isPrn && (
        <div className="registration__field">
          <label htmlFor="medication-field-prn-maximum">
            {t('medication:field.prnMaxDose24h')}
          </label>

          <p id="medication-field-prn-maximum-hint" className="registration__hint">
            {t('medication:fieldHint.prnMaxDose24h')}
          </p>

          <input
            id="medication-field-prn-maximum"
            // Text, and the hint says to write the ceiling as it was ordered. Nothing here derives it
            // from the dose and the frequency — that is dose calculation (#46 point 8).
            type="text"
            value={draft.prnMaxDose24h}
            disabled={busy}
            aria-describedby="medication-field-prn-maximum-hint"
            maxLength={MAXIMUM_SHORT_FIELD_LENGTH}
            onChange={(event) => update('prnMaxDose24h', event.target.value)}
          />
        </div>
      )}

      <div className="registration__field">
        <label htmlFor="medication-field-indication">
          {t('patient:medication.columnIndication')}
        </label>

        <p id="medication-field-indication-hint" className="registration__hint">
          {/* indication = ordinationsorsak, mandatory under HSLF-FS 2017:37 */}
          {t('medication:fieldHint.indication')}
        </p>

        <textarea
          id="medication-field-indication"
          value={draft.indication}
          disabled={busy}
          rows={2}
          aria-describedby="medication-field-indication-hint"
          maxLength={MAXIMUM_INDICATION_LENGTH}
          onChange={(event) => update('indication', event.target.value)}
        />
      </div>

      <div className="registration__field registration__field--datetime">
        <label htmlFor="medication-field-end-at">
          {t('medication:field.endAt')}
          <span className="registration__optional"> ({t('medication:screen.optional')})</span>
        </label>

        <p id="medication-field-end-at-hint" className="registration__hint">
          {t('medication:fieldHint.endAt')}
        </p>

        <input
          id="medication-field-end-at"
          // Text, not `datetime-local` — see `toLocalInput`. No `maxLength` either: a limit of
          // sixteen would cut `2026-10-15 08:00:00` to a valid-looking `2026-10-15 08:00` at the
          // keyboard, which is the reservnummer field's lesson (registrationDraft.ts). Too long is
          // refused instead.
          type="text"
          value={draft.endAt}
          disabled={busy}
          aria-describedby="medication-field-end-at-hint"
          onChange={(event) => update('endAt', event.target.value)}
        />
      </div>

      <div className="registration__field">
        <label htmlFor="medication-field-status">{t('medication:field.status')}</label>

        <p id="medication-field-status-hint" className="registration__hint">
          {t('medication:fieldHint.status')}
        </p>

        <select
          id="medication-field-status"
          value={draft.status}
          disabled={busy}
          aria-describedby="medication-field-status-hint"
          onChange={(event) => update('status', event.target.value)}
        >
          {MEDICATION_STATUSES.map((status) => (
            <option key={status} value={status}>
              {medicationStatusLabel(status, t)}
            </option>
          ))}
        </select>
      </div>

      <div aria-live="assertive">
        {notice !== null && (
          <div className="registration__notice" role="alert">
            <p>
              {notice.kind === 'unavailable'
                ? medicationUnavailableMessage(t)
                : medicationRefusalMessage(notice.code, notice.params, t)}
            </p>
          </div>
        )}
      </div>

      <footer className="registration__actions">
        <p className="registration__final">{t('medication:screen.appendOnly')}</p>

        <button type="button" className="registration__register" disabled={busy} onClick={submit}>
          {busy ? t('medication:screen.saving') : t('medication:screen.save')}
        </button>

        <button type="button" disabled={busy} onClick={close}>
          {t('medication:screen.cancel')}
        </button>
      </footer>

      {confirmingDiscard && (
        <div
          className="registration__discard"
          role="alertdialog"
          aria-label={t('medication:screen.discardQuestion')}
        >
          <p>{t('medication:screen.discardQuestion')}</p>

          <button type="button" onClick={() => setConfirmingDiscard(false)}>
            {t('medication:screen.discardCancel')}
          </button>
          <button type="button" onClick={onClose}>
            {t('medication:screen.discardConfirm')}
          </button>
        </div>
      )}
    </section>
  )
}
